import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/site/Seo";

export default function Estimator() {
  const [form, setForm] = useState({
    project_type: "Renovation",
    square_meters: 120,
    finish_level: "premium",
    bedrooms: 3,
    location: "London",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(v) : v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/estimate", form);
      setResult(data.estimate);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not generate estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="estimator-page">
      <Seo
        title="AI Cost Estimator — Instant Build Cost"
        description="Free AI-powered cost estimator for residential construction projects in London. Get an indicative ballpark cost for new builds, renovations and extensions in under 30 seconds."
        path="/estimator"
      />
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28">
          <div className="overline mb-6 flex items-center gap-2"><Sparkles size={14} className="text-[#C5A059]" /> AI Cost Estimator</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            Get an indicative<br /> cost in 20 seconds.
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-2xl">
            Tell us about your project and our AI will draft a ballpark estimate based on a decade of
            UK residential build data. For an accurate quotation we'll still need a site visit.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <form onSubmit={submit} className="lg:col-span-5 border border-slate-200 p-8" data-testid="estimator-form">
            <div className="space-y-5">
              <div>
                <label className="overline">Project type</label>
                <select data-testid="est-project-type" className="field mt-2" value={form.project_type} onChange={update("project_type")}>
                  <option>New Build</option>
                  <option>Renovation</option>
                  <option>Extension</option>
                  <option>Design-Build</option>
                </select>
              </div>
              <div>
                <label className="overline">Size (square metres)</label>
                <input data-testid="est-sqm" type="number" min={10} step={5} className="field mt-2" value={form.square_meters} onChange={update("square_meters")} required />
              </div>
              <div>
                <label className="overline">Finish level</label>
                <select data-testid="est-finish" className="field mt-2" value={form.finish_level} onChange={update("finish_level")}>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="luxury">Luxury</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline">Bedrooms</label>
                  <input data-testid="est-bedrooms" type="number" min={0} max={20} className="field mt-2" value={form.bedrooms} onChange={update("bedrooms")} />
                </div>
                <div>
                  <label className="overline">Location</label>
                  <input data-testid="est-location" className="field mt-2" value={form.location} onChange={update("location")} placeholder="London" />
                </div>
              </div>
              <div>
                <label className="overline">Notes (optional)</label>
                <textarea data-testid="est-notes" rows={3} className="field mt-2" value={form.notes} onChange={update("notes")} placeholder="Anything special — basement, pool, heritage constraints…" />
              </div>

              <button type="submit" data-testid="est-submit" disabled={loading} className="btn-accent w-full justify-center mt-2">
                {loading ? (<><Loader2 className="animate-spin" size={16} /> Calculating…</>) : (<><Sparkles size={16} /> Generate AI Estimate</>)}
              </button>
              <p className="text-xs text-slate-500 leading-relaxed">
                Estimates are AI-generated indicative ranges based on typical UK residential projects.
                A final, site-specific quotation requires a consultation.
              </p>
            </div>
          </form>

          {/* Result panel — "blueprint" look */}
          <div className="lg:col-span-7" data-testid="estimator-result">
            {!result && !loading && (
              <div className="border border-dashed border-slate-300 h-full min-h-[420px] flex flex-col items-center justify-center text-center p-10 bg-slate-50">
                <Sparkles size={32} className="text-[#C5A059]" strokeWidth={1.5} />
                <h3 className="font-display text-2xl font-semibold text-slate-900 mt-4">Your estimate will appear here</h3>
                <p className="text-slate-600 mt-2 max-w-md">
                  Fill in your project details and hit "Generate AI Estimate". Powered by Claude Sonnet 4.5.
                </p>
              </div>
            )}
            {loading && (
              <div className="border border-slate-200 h-full min-h-[420px] flex items-center justify-center bg-white" data-testid="estimator-loading">
                <div className="flex flex-col items-center text-slate-500">
                  <Loader2 className="animate-spin text-[#C5A059]" size={32} />
                  <div className="mt-4">Drafting your estimate…</div>
                </div>
              </div>
            )}
            {result && (
              <div className="bg-slate-900 text-slate-100 p-10 relative overflow-hidden" data-testid="estimator-output">
                <div className="absolute top-0 right-0 w-40 h-40 border-l border-b border-slate-700 -mr-20 -mt-20 rotate-12 pointer-events-none" />
                <div className="overline text-[#C5A059] mb-4 flex items-center gap-2"><Sparkles size={12} /> Indicative AI estimate</div>
                <h3 className="font-display text-3xl font-bold mb-6">Prime Build Design · Project Estimate</h3>
                <div className="whitespace-pre-wrap leading-relaxed text-slate-200 font-body text-[15px]">
                  {result}
                </div>
                <div className="mt-10 pt-6 border-t border-slate-800 flex flex-wrap gap-3">
                  <a href="/contact" className="btn-accent" data-testid="est-cta-contact">Request a site visit</a>
                  <button onClick={() => { setResult(null); }} className="btn-ghost text-white border-white hover:bg-white hover:text-slate-900" data-testid="est-reset">
                    Run another estimate
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
